import { formatSpeechText } from '@/utils/speechTextFormatter';
import type { Editor } from '@tiptap/core';

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: {
    [key: number]: {
      [key: number]: {
        transcript: string;
      };
      isFinal: boolean;
    };
    length: number;
  };
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  start(): void;
  stop(): void;
}

export interface UseSpeechRecognitionOptions {
  onError?: (message: string) => void;
  onStart?: () => void;
  onEnd?: () => void;
}

export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {},
) {
  const isRecording = ref(false);
  const isSupported = ref(false);
  const recognition = ref<SpeechRecognition | null>(null);
  const interimTranscript = ref('');

  const initializeRecognition = () => {
    // Check for Speech Recognition API (standard or webkit-prefixed)
    const WindowWithSpeech = window as Window & {
      SpeechRecognition?: new () => SpeechRecognition;
      webkitSpeechRecognition?: new () => SpeechRecognition;
    };

    const SpeechRecognition =
      WindowWithSpeech.SpeechRecognition ||
      WindowWithSpeech.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      return false;
    }

    isSupported.value = true;
    recognition.value = new SpeechRecognition();

    recognition.value.continuous = true;
    recognition.value.interimResults = true;
    recognition.value.lang = navigator.language || 'en-US';

    recognition.value.onstart = () => {
      isRecording.value = true;
      interimTranscript.value = '';
      options.onStart?.();
    };

    recognition.value.onend = () => {
      isRecording.value = false;
      interimTranscript.value = '';
      options.onEnd?.();
    };

    recognition.value.onerror = (event: SpeechRecognitionErrorEvent) => {
      isRecording.value = false;
      interimTranscript.value = '';

      let errorMessage = 'Speech recognition error';
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = 'No microphone found or microphone access denied.';
          break;
        case 'not-allowed':
          errorMessage =
            'Microphone permission denied. Please allow microphone access.';
          break;
        case 'network':
          errorMessage =
            'Network error occurred. Please check your connection.';
          break;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }

      options.onError?.(errorMessage);
    };

    return true;
  };

  const startDictation = (editor: Editor | null) => {
    if (!editor || !recognition.value) {
      if (!isSupported.value) {
        options.onError?.(
          'Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.',
        );
      }

      return;
    }

    editor.chain().focus().run();

    recognition.value.onresult = (event: SpeechRecognitionEvent) => {
      if (!editor) {
        return;
      }

      let finalTranscript = '';
      let tempInterimTranscript = '';

      // Collect transcripts from recognition results
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          tempInterimTranscript += transcript;
        }
      }

      // Handle final (confirmed) transcript
      if (finalTranscript) {
        const currentPos = editor.state.selection.from;
        const doc = editor.state.doc;

        // Calculate the actual position where new text will be inserted
        const insertPosition = interimTranscript.value
          ? currentPos - interimTranscript.value.length
          : currentPos;

        // Get text before the insertion point for context
        const textBeforeCursor =
          insertPosition > 0
            ? doc.textBetween(Math.max(0, insertPosition - 10), insertPosition)
            : '';

        // Check if this is the first insertion
        const isFirstInsertion = doc.textContent.trim().length === 0;

        // Format the text with all spacing and capitalization logic
        const formattedText = formatSpeechText(finalTranscript, {
          textBefore: textBeforeCursor,
          isFirstInsertion,
        });

        // Replace interim text with formatted final text, or insert new text
        if (interimTranscript.value) {
          const interimLength = interimTranscript.value.length;
          editor
            .chain()
            .focus()
            .setTextSelection({
              from: currentPos - interimLength,
              to: currentPos,
            })
            .insertContent(formattedText)
            .run();
        } else {
          editor.chain().focus().insertContent(formattedText).run();
        }

        interimTranscript.value = '';
      }

      // Handle interim (temporary) transcript
      if (
        tempInterimTranscript &&
        tempInterimTranscript !== interimTranscript.value
      ) {
        const currentPos = editor.state.selection.from;

        // For interim text, we don't format it to avoid confusing the user
        if (interimTranscript.value) {
          const interimLength = interimTranscript.value.length;
          editor
            .chain()
            .focus()
            .setTextSelection({
              from: currentPos - interimLength,
              to: currentPos,
            })
            .insertContent(tempInterimTranscript)
            .run();
        } else {
          editor.chain().focus().insertContent(tempInterimTranscript).run();
        }

        interimTranscript.value = tempInterimTranscript;
      }
    };

    recognition.value.start();
  };

  const stopDictation = () => {
    if (recognition.value && isRecording.value) {
      recognition.value.stop();
    }
  };

  const toggleDictation = (editor: Editor | null) => {
    if (!isSupported.value || !recognition.value || !editor) {
      if (!isSupported.value) {
        options.onError?.(
          'Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.',
        );
      }

      return;
    }

    if (isRecording.value) {
      stopDictation();
    } else {
      startDictation(editor);
    }
  };

  onMounted(() => {
    initializeRecognition();
  });

  onUnmounted(() => {
    stopDictation();
  });

  return {
    isRecording: readonly(isRecording),
    isSupported: readonly(isSupported),
    toggleDictation,
    startDictation,
    stopDictation,
  };
}
